import { Avatar, Segmented, Toggle, useToast } from '@pa/ui'
import { Link } from '@tanstack/react-router'
import {
  AlertTriangle,
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  MapPin,
  Network,
  Power,
  Stethoscope,
  Video,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import {
  buildBookingMap,
  issueKeys,
  type MapBy,
  type MapKind,
  type MapNode,
  type SettingsTab,
} from '../model/bookingMap'
import type { Settings } from '../model/types'
import { useSettingsDraft } from '../SettingsDraftProvider'

const KIND_PLURAL: Record<MapKind, [string, string]> = {
  location: ['location', 'locations'],
  provider: ['provider', 'providers'],
  'visit-type': ['visit type', 'visit types'],
}
const plural = (n: number, kind: MapKind) => `${String(n)} ${KIND_PLURAL[kind][n === 1 ? 0 : 1]}`
const hasIssue = (n: MapNode): boolean => n.state === 'red' || n.state === 'amber' || n.children.some(hasIssue)

export const BOOKING_MAP_COPY = {
  readByLocation: 'Read as: who patients can see at each location, and what they take there. Patients choose',
  readByLocationOrder: 'location → visit type → provider',
  readByVisitType: 'Read as: where each visit type can be booked, and with whom.',
  allGood: 'Everything patients can see is bookable',
  unsaved: 'Includes unsaved changes',
  empty: 'Nothing is bookable yet. Turn on a visit type, a location and a provider.',
  unplacedTitle: "Turned on, but patients can't reach them",
  unplacedHint: "They don't connect to anything above, so they never show up in booking.",
  off: "Off · patients don't see it",
  turnedOff: (name: string) => `${name} turned off. Save to apply.`,
} as const

const TAB_ROUTE = {
  'visit-types': '/clinics/$clinicId/settings/booking/visit-types',
  locations: '/clinics/$clinicId/settings/booking/locations',
  providers: '/clinics/$clinicId/settings/booking/providers',
} as const satisfies Record<SettingsTab, string>
const TAB_NAME: Record<SettingsTab, string> = {
  'visit-types': 'Visit types',
  locations: 'Locations',
  providers: 'Providers',
}

/**
 * C-05 Booking map (UserFlows C-05, Frontend §6.6), ported from the mockup's `admin/BookingMap.tsx`: a tree of what
 * patients can book built from the draft (`buildBookingMap`), by location (default) or by visit type, with the
 * summary, the issue buttons, Turn off, the "can't reach them" card, Expand / Collapse all and an arrow to each row
 * on C-06 / C-07 / C-08. `by` and `hidden` live in the URL (Frontend §6.1).
 */
export function BookingMapTab({
  by,
  showHidden,
  onSearch,
}: {
  by: MapBy
  showHidden: boolean
  onSearch: (next: { by?: MapBy; hidden?: boolean }) => void
}) {
  const { draft, update, dirty, clinicId } = useSettingsDraft()
  const toast = useToast()
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const [flash, setFlash] = useState<{ key: string; n: number } | null>(null)
  const map = useMemo(() => buildBookingMap(draft, by, showHidden), [draft, by, showHidden])
  const { counts } = map
  // A handful of roots: show everything. A long list: open only the branches that need attention.
  const isOpen = (n: MapNode) => open[n.key] ?? (map.roots.length <= 4 || hasIssue(n))

  // Jump to the first item with this problem: open its branch, then scroll to it and flash it (after the render).
  useEffect(() => {
    if (!flash) return
    const el = document.querySelector<HTMLElement>(`[data-node="${CSS.escape(flash.key)}"]`)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el.classList.remove('row-focus')
    void el.getBoundingClientRect() // restart the animation
    el.classList.add('row-focus')
  }, [flash])

  const showIssue = (state: 'red' | 'amber') => {
    const key = issueKeys(map, state)[0]
    if (!key) return
    const root = key.split('/')[0]
    if (!key.startsWith('unplaced/') && root) setOpen((o) => ({ ...o, [root]: true }))
    setFlash((f) => ({ key, n: (f?.n ?? 0) + 1 }))
  }

  const turnOff = (n: MapNode) => {
    update((d) => {
      if (n.kind === 'visit-type')
        d.visitTypes = d.visitTypes.map((v) => (v.id === n.id ? { ...v, enabled: false } : v))
      if (n.kind === 'location') d.locations = d.locations.map((l) => (l.id === n.id ? { ...l, visible: false } : l))
      if (n.kind === 'provider') d.providers = d.providers.map((p) => (p.id === n.id ? { ...p, visible: false } : p))
      return d
    })
    toast(BOOKING_MAP_COPY.turnedOff(n.label), 'info')
  }

  const nodeProps: Omit<NodeProps, 'node' | 'depth'> = {
    draft,
    clinicId,
    isOpen,
    toggle: (n: MapNode) => {
      setOpen((o) => ({ ...o, [n.key]: !isOpen(n) }))
    },
    turnOff,
  }

  return (
    <div className="bm">
      <div className="bm-summary card" role="group" aria-label="Summary">
        <div className="bm-counts">
          <span>
            <b>{counts.locations}</b> {KIND_PLURAL.location[counts.locations === 1 ? 0 : 1]}
          </span>
          <span>
            <b>{counts.providers}</b> {KIND_PLURAL.provider[counts.providers === 1 ? 0 : 1]}
          </span>
          <span>
            <b>{counts.visitTypes}</b> {KIND_PLURAL['visit-type'][counts.visitTypes === 1 ? 0 : 1]} on
          </span>
        </div>
        <div className="bm-issues">
          {counts.red > 0 && (
            <button
              type="button"
              className="bm-issue red"
              onClick={() => {
                showIssue('red')
              }}
            >
              <CircleAlert aria-hidden /> {counts.red} missing in PF <ChevronRight aria-hidden />
            </button>
          )}
          {counts.amber > 0 && (
            <button
              type="button"
              className="bm-issue amber"
              onClick={() => {
                showIssue('amber')
              }}
            >
              <AlertTriangle aria-hidden /> {counts.amber} {counts.amber === 1 ? 'dead end' : 'dead ends'}{' '}
              <ChevronRight aria-hidden />
            </button>
          )}
          {counts.red + counts.amber === 0 && <span className="bm-allgood">{BOOKING_MAP_COPY.allGood}</span>}
        </div>
      </div>

      <div className="card">
        <div className="toolbar filters bm-toolbar">
          <Segmented
            label="View"
            value={by}
            onChange={(v) => {
              onSearch({ by: v })
            }}
            options={[
              { value: 'location', label: 'By location' },
              { value: 'visit-type', label: 'By visit type' },
            ]}
          />
          <span className="bm-hidden-toggle">
            <Toggle
              checked={showHidden}
              onChange={(v) => {
                onSearch({ hidden: v })
              }}
              label="Show hidden and off items"
            />
            <span aria-hidden>Show hidden and off items</span>
          </span>
          <span className="grow" />
          {dirty && <span className="tag tag-blue">{BOOKING_MAP_COPY.unsaved}</span>}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setOpen(Object.fromEntries(map.roots.map((r) => [r.key, true])))
            }}
          >
            Expand all
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setOpen(Object.fromEntries(map.roots.map((r) => [r.key, false])))
            }}
          >
            Collapse all
          </button>
        </div>
        <p className="bm-read hint">
          {by === 'location' ? (
            <>
              {BOOKING_MAP_COPY.readByLocation} <b>{BOOKING_MAP_COPY.readByLocationOrder}</b>.
            </>
          ) : (
            BOOKING_MAP_COPY.readByVisitType
          )}
        </p>

        <div className="bm-tree">
          {map.roots.map((r) => (
            <Branch key={r.key} node={r} depth={0} {...nodeProps} />
          ))}
          {!map.roots.length && (
            <div className="empty">
              <Network aria-hidden />
              <div>{BOOKING_MAP_COPY.empty}</div>
            </div>
          )}
        </div>
      </div>

      {map.unplaced.length > 0 && (
        <section className="card bm-unplaced" aria-labelledby="bm-unplaced-title">
          <div className="bm-unplaced-head">
            <AlertTriangle aria-hidden />
            <div>
              <h2 id="bm-unplaced-title">{BOOKING_MAP_COPY.unplacedTitle}</h2>
              <span className="hint">{BOOKING_MAP_COPY.unplacedHint}</span>
            </div>
          </div>
          <div className="bm-unplaced-list">
            {map.unplaced.map((n) => (
              <Node key={n.key} node={n} depth={-1} {...nodeProps} />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

interface NodeProps {
  node: MapNode
  depth: number
  draft: Settings
  clinicId: string
  isOpen: (n: MapNode) => boolean
  toggle: (n: MapNode) => void
  turnOff: (n: MapNode) => void
}

function Branch(p: NodeProps) {
  const { node, depth } = p
  const collapsible = depth === 0 && node.children.length > 0
  const shown = !collapsible || p.isOpen(node)
  return (
    <div className={`bm-branch d${String(depth)}`}>
      <Node {...p} />
      {shown &&
        node.children.length > 0 &&
        // The last level is drawn as one wrapped group, so a provider with ten visit types stays compact.
        (node.children.every((c) => !c.children.length) ? (
          <div className="bm-kids leafy">
            <div className="bm-leafbox">
              {node.children.map((c) => (
                <Node key={c.key} {...p} node={c} depth={depth + 1} />
              ))}
            </div>
          </div>
        ) : (
          <div className="bm-kids">
            {node.children.map((c) => (
              <Branch key={c.key} {...p} node={c} depth={depth + 1} />
            ))}
          </div>
        ))}
    </div>
  )
}

function Node({ node, depth, draft, clinicId, isOpen, toggle, turnOff }: NodeProps) {
  const collapsible = depth === 0 && node.children.length > 0
  const open = collapsible && isOpen(node)
  const prov = node.kind === 'provider' ? draft.providers.find((x) => x.id === node.id) : undefined
  const vt = node.kind === 'visit-type' ? draft.visitTypes.find((x) => x.id === node.id) : undefined

  // Collapsed root: say what's inside instead.
  let sub = node.sub
  if (collapsible && !open) {
    const seen: Partial<Record<MapKind, Set<string>>> = {}
    const walk = (n: MapNode) => {
      n.children.forEach((c) => {
        if (c.state !== 'off') (seen[c.kind] ??= new Set()).add(c.id)
        walk(c)
      })
    }
    walk(node)
    const parts = (Object.entries(seen) as [MapKind, Set<string>][]).map(([k, ids]) => plural(ids.size, k))
    if (parts.length) sub = parts.join(' · ')
  }

  return (
    <div className={`bm-node k-${node.kind} s-${node.state}`} data-node={node.key}>
      {collapsible && (
        <button
          type="button"
          className="bm-chev"
          aria-expanded={open}
          aria-label={`${open ? 'Collapse' : 'Expand'} ${node.label}`}
          onClick={() => {
            toggle(node)
          }}
        >
          {open ? <ChevronDown aria-hidden /> : <ChevronRight aria-hidden />}
        </button>
      )}
      <span className="bm-ic" aria-hidden>
        {node.kind === 'location' && <MapPin />}
        {node.kind === 'provider' && <Avatar name={prov?.name ?? node.label} color={prov?.color} size="sm" />}
        {node.kind === 'visit-type' && (vt?.telehealth ? <Video size={15} /> : <Stethoscope size={15} />)}
      </span>
      <span className="bm-text">
        <b>{node.label}</b>
        {sub && <small>{sub}</small>}
        {node.state === 'off' && <span className="bm-reason">{BOOKING_MAP_COPY.off}</span>}
        {node.reason && (
          <span className="bm-reason">
            {node.state === 'red' ? <CircleAlert aria-hidden /> : <AlertTriangle aria-hidden />} {node.reason}
          </span>
        )}
        {node.state === 'red' && (
          <button
            type="button"
            className="bm-off"
            aria-label={`Turn off ${node.label}`}
            onClick={() => {
              turnOff(node)
            }}
          >
            <Power aria-hidden /> Turn off
          </button>
        )}
      </span>
      <span className="bm-actions">
        <Link
          className="bm-go"
          title="Take me there"
          aria-label={`Take me to ${node.label} in ${TAB_NAME[node.tab]}`}
          to={TAB_ROUTE[node.tab]}
          params={{ clinicId }}
          search={{ focus: node.id }}
        >
          <ArrowUpRight aria-hidden />
        </Link>
      </span>
    </div>
  )
}
